import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { DocLinkTarget, LinkTarget } from "../../lib/api/models";
import { qk, refreshInventory, useDocLinks, useItems, useSites } from "../../lib/queries";

const keyOf = (t: LinkTarget) => `${t.targetType}:${t.targetId}`;
const itemOption = (i: { id: string; name: string; kind: string }) => ({
  value: `item:${i.id}`,
  label: `${i.name} (${i.kind})`,
});
const fromKey = (k: string): LinkTarget => {
  const [type, id] = k.split(":");
  return { targetType: type === "site" ? "site" : "item", targetId: id ?? "" };
};

/**
 * "Covers": the sites, devices and network equipment of the document's own client that it is about (spec §3.1).
 * Each change replaces the whole set at once (`PUT …/links`); it is not tied to the edit lock and writes no revision.
 */
export function CoversPicker({ docId, clientId, readOnly }: { docId: string; clientId: string; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const { data: links, isLoading } = useDocLinks(docId);
  const { data: sites = [] } = useSites(readOnly ? undefined : clientId);
  const { data: items = [] } = useItems(readOnly ? undefined : clientId);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const current = links ?? [];
  const linked = new Set(current.map(keyOf));

  const save = async (next: LinkTarget[]) => {
    setBusy(true);
    try {
      queryClient.setQueryData<DocLinkTarget[]>(qk.links(docId), await api.links.set(docId, next));
      await refreshInventory(queryClient, clientId);
    } catch (e) {
      toast(`Covers could not be saved: ${e instanceof Error ? e.message : "unknown error"}`, "error");
      await queryClient.invalidateQueries({ queryKey: qk.links(docId) });
    } finally {
      setBusy(false);
    }
  };
  const add = () => {
    if (!choice) return;
    void save([...current, fromKey(choice)]);
    setChoice("");
  };

  const devices = items.filter((i) => i.category === "device" && !linked.has(`item:${i.id}`));
  const network = items.filter((i) => i.category === "network" && !linked.has(`item:${i.id}`));
  const freeSites = sites.filter((s) => !linked.has(`site:${s.id}`));
  return (
    <section aria-label="Covers" className="mt-5 grid gap-2 border-t border-line pt-4">
      <h3 className="text-sm font-semibold text-ink">Covers</h3>
      <p className="text-xs text-ink-3">
        The client's sites, devices and network equipment this document is about. Changes save at once and are not part
        of the document's history.
      </p>
      {isLoading ? <p className="text-sm text-ink-3">Loading…</p> : null}
      {links && current.length === 0 ? <p className="text-sm text-ink-3">Nothing linked yet.</p> : null}
      <ul className="grid gap-1">
        {current.map((l) => (
          <li key={keyOf(l)} className="flex items-center gap-2 rounded-md bg-column px-2.5 py-1.5 text-sm">
            <span className="flex-1 text-ink">{l.name}</span>
            <span className="text-xs text-ink-3">{l.targetType === "site" ? "Site" : l.kind}</span>
            {readOnly ? null : (
              <button
                type="button"
                aria-label={`Remove ${l.name}`}
                disabled={busy}
                onClick={() => void save(current.filter((x) => keyOf(x) !== keyOf(l)))}
                className="grid h-6 w-6 place-items-center rounded text-ink-3 hover:bg-danger-wash hover:text-danger"
              >
                <X size={13} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {readOnly ? null : (
        <div className="flex items-center gap-2">
          <Select
            aria-label="Add a site or item"
            value={choice}
            onChange={setChoice}
            options={[
              { value: "", label: "Add a site, device or network item…" },
              ...[
                { label: "Sites", options: freeSites.map((s) => ({ value: `site:${s.id}`, label: s.name })) },
                { label: "Devices", options: devices.map(itemOption) },
                { label: "Network and printers", options: network.map(itemOption) },
              ].filter((g) => g.options.length > 0),
            ]}
          />
          <Button onClick={add} disabled={!choice || busy}>
            Add
          </Button>
        </div>
      )}
    </section>
  );
}
